import Select from 'react-select';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from '@components/Shared/Loader/Loading';
import { Card, CardFooter, CardHeader } from '@components/card/card';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { initReport, getReportPagedData, selectReport, setReportRequest } from '@store/DynamicSlice/reportTableSlice';
import ReportExportExcelModal, { type ExportColumn } from '@Helper/ReportPaged/ReportExportExcelModal';
import ReportPagination from '@Helper/ReportPaged/ReportPagination';
import useGlChartLookups from '@hooks/Finance/ChartAccountsDetails/useGlChartLookups';
import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
import useGetSuppliersList from '@hooks/NTX/Peoples/useGetSuppliersList';

/** =========================
 * INNER REQUEST SHAPE
 * ========================= */
type SupplierAccountStatementRequestDto = {
  supplierId: string | null; // REQUIRED
  fromDate?: string | null;
  toDate?: string | null;
  organizationId?: string | null;
  branchId?: string | null;
  includePurEffect0?: boolean;
  invoicePostedFlag?: boolean | null;
  invoiceCancelledFlag?: boolean | null;
  invoiceHoldFlag?: boolean | null;
  paymentStatus?: string | null;
  paymentTypeId?: string | null;
  purCodeLike?: string | null;
  invCodeLike?: string | null;
  invoiceCodeLike?: string | null;
  invoiceNumLike?: string | null;
  supplierInvoiceNumLike?: string | null;
  paymentCodeLike?: string | null;
  onlyOpenInvoices?: boolean;
  onlyUnappliedPrepayments?: boolean;
  pageIndex?: number;
  pageSize?: number;
};

/** =========================
 * RESPONSE SHAPE (Array items)
 * ========================= */
type RowDto = {
  rowId: number;
  section: string | null;
  levelOrder: number;
  contractPurId: string | null;
  docType: string | null;
  docId: string | null;
  organizationId: string | null;
  organizationCode: string | null;
  organizationName: string | null;
  organizationName2: string | null;
  docCode: string | null;
  docDate: string | null;
  purCode: string | null;
  invCode: string | null;
  invoiceCode: string | null;
  invoiceNum: string | null;
  supplierInvoiceNum: string | null;
  paymentCode: string | null;
  debit: number;
  credit: number;
  docAmount: number | null;
  appliedAmount: number | null;
  unappliedAmount: number | null;
  openAmount: number | null;
  notes: string | null;
  postedFlag: boolean | null;
  cancelledFlag: boolean | null;
  holdFlag: boolean | null;
  paymentFlag: boolean | null;
  payStatus: string | null;
  payType: string | null;
};

const REPORT_KEY = 'RPT_SupplierAccountStatement';
const ENDPOINT = 'FinReports/GetSupplierAccountStatementPaged';

const safe = (v: any, fallback = '—') => (v === null || v === undefined || v === '' ? fallback : String(v));

const formatNumber = (num: number | null | undefined, digits: number = 2) => {
  if (num === null || num === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(num);
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

const getOptId = (opt: any): string | null => {
  const v = opt?.value ?? opt?.id ?? opt?.ID ?? opt?.key ?? null;
  return v ? String(v) : null;
};

const isoOrNull = (d: any) => {
  if (!d) return null;
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return `${d}T00:00:00.000Z`;
  if (d instanceof Date) return d.toISOString();
  return null;
};

const cleanNulls = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    // نتجاهل القيم الفارغة، ولكن نحتفظ بالـ boolean (حتى لو كانت false)
    if (v === null || v === undefined || v === '') continue;
    out[k] = v;
  }
  return out;
};

const SupplierAccountStatement_Paged = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  // options

  const { OperatingUnitOptions: OptionsBranches, loading: loadingBranches } = useGetOperatingUnitWithPermission();

  const { OptionSuppliers: OptionsSuppliers, loading: loadingSuppliers } = useGetSuppliersList();

  // Filters state
  const [supplierFilter, setSupplierFilter] = useState<any>(null); // إلزامي
  const [organizationFilter, setOrganizationFilter] = useState<any>(null);

  // Date range
  const [fromDate, setFromDate] = useState<any>(null);
  const [toDate, setToDate] = useState<any>(null);

  // Additional Filters
  const [onlyOpenInvoices, setOnlyOpenInvoices] = useState<boolean>(false);
  const [purCodeLike, setPurCodeLike] = useState<string>('');

  const [showExport, setShowExport] = useState(false);

  // report state
  const report = useAppSelector(s => selectReport(s as any, REPORT_KEY));
  const items = (report?.items ?? []) as RowDto[];
  const meta = report?.meta ?? null;
  const loading = report?.loading ?? false;
  const error = report?.error ?? null;
  const isAr = i18n.language !== 'en';

  const exportColumns: ExportColumn<RowDto>[] = [
    { headerAr: 'نوع المستند', headerEn: 'Doc Type', value: r => r.docType || '' },
    { headerAr: 'رقم المستند', headerEn: 'Doc Code', value: r => r.docCode || '' },
    { headerAr: 'التاريخ', headerEn: 'Date', value: r => formatDate(r.docDate) },
    {
      headerAr: 'الفرع/المنظمة',
      headerEn: 'Organization',
      value: r => (isAr ? r.organizationName2 || r.organizationName : r.organizationName || r.organizationName2) || ''
    },
    { headerAr: 'مدين', headerEn: 'Debit', value: r => r.debit || 0 },
    { headerAr: 'دائن', headerEn: 'Credit', value: r => r.credit || 0 },
    { headerAr: 'المبلغ المفتوح', headerEn: 'Open Amount', value: r => r.openAmount || 0 },
    { headerAr: 'الوصف/الملاحظات', headerEn: 'Notes', value: r => r.notes || '' }
  ];

  useEffect(() => {
    const initialRequest: SupplierAccountStatementRequestDto = {
      supplierId: null, // لا يوجد مورد في البداية
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(
      initReport({
        reportKey: REPORT_KEY,
        endpoint: ENDPOINT,
        initialRequest,
        // skipInitialFetch: true // لن نقوم بالجلب حتى يختار المستخدم المورد
      })
    );
    // ❌ لا نستدعي getReportPagedData هنا لأن المورد إلزامي
  }, [dispatch]);

  const applyFilters = () => {
    const suppId = getOptId(supplierFilter);
    if (!suppId) return; // حماية إضافية

    const orgId = getOptId(organizationFilter);
    const prevReq = (((report as any)?.request ?? {}) as SupplierAccountStatementRequestDto) || {};

    const nextReq: SupplierAccountStatementRequestDto = {
      ...prevReq,
      pageIndex: 0,
      pageSize: prevReq.pageSize ?? 50,
      supplierId: suppId,
      organizationId: orgId,
      fromDate: isoOrNull(fromDate),
      toDate: isoOrNull(toDate),
      onlyOpenInvoices: onlyOpenInvoices || undefined,
      purCodeLike: purCodeLike || null
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: cleanNulls(nextReq) as any }));
    dispatch(getReportPagedData({ reportKey: REPORT_KEY }));
  };

  const clearFilters = () => {
    setSupplierFilter(null);
    setOrganizationFilter(null);
    setFromDate(null);
    setToDate(null);
    setOnlyOpenInvoices(false);
    setPurCodeLike('');

    const resetReq: SupplierAccountStatementRequestDto = {
      supplierId: null,
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: resetReq as any }));
    // ❌ لا نستدعي الجلب بعد المسح لعدم وجود مورد
  };

  const totals = useMemo(() => {
    return items.reduce(
      (acc, row) => {
        acc.totalDebit += row.debit || 0;
        acc.totalCredit += row.credit || 0;
        acc.totalOpenAmount += row.openAmount || 0;
        return acc;
      },
      { totalDebit: 0, totalCredit: 0, totalOpenAmount: 0 }
    );
  }, [items]);

  // دالة للحصول على لون وأيقونة بناءً على نوع المستند
  const getDocTypeBadge = (docType: string | null) => {
    switch (docType) {
      case 'PUR':
        return (
          <span className='badge bg-primary bg-opacity-10 text-primary border border-primary'>
            <i className='fas fa-shopping-cart me-1'></i> {docType}
          </span>
        );
      case 'INV':
        return (
          <span className='badge bg-info bg-opacity-10 text-info border border-info'>
            <i className='fas fa-box-open me-1'></i> {docType}
          </span>
        );
      case 'AP':
        return (
          <span className='badge bg-warning bg-opacity-10 text-warning border border-warning'>
            <i className='fas fa-file-invoice-dollar me-1'></i> {docType}
          </span>
        );
      default:
        return <span className='badge bg-secondary'>{safe(docType)}</span>;
    }
  };

  return (
    <>
      <CardHeader className='mb-3'>
        <div className='row g-2 align-items-center mb-2'>
          {/* المورد (إلزامي) */}
          <div className='col-lg-4'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-truck text-danger'></i>
                <span className='text-danger ms-1'>*</span>
              </span>
              <Select
                isLoading={loadingSuppliers}
                value={supplierFilter}
                onChange={setSupplierFilter}
                options={OptionsSuppliers} // TODO: ربط الموردين هنا
                placeholder={isAr ? 'المورد (مطلوب)' : 'Supplier (Required)'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* الفرع / المنظمة */}
          <div className='col-lg-4'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-building text-primary'></i>
              </span>
              <Select
                isLoading={loadingBranches}
                value={organizationFilter}
                onChange={setOrganizationFilter}
                options={OptionsBranches}
                placeholder={isAr ? 'الفرع / المنظمة' : 'Organization'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* من تاريخ */}
          <div className='col-lg-2'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={fromDate || ''}
                onChange={e => setFromDate(e.target.value)}
                placeholder={isAr ? 'من تاريخ' : 'From Date'}
              />
            </div>
          </div>

          {/* إلى تاريخ */}
          <div className='col-lg-2'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={toDate || ''}
                onChange={e => setToDate(e.target.value)}
                min={fromDate || undefined}
                placeholder={isAr ? 'إلى تاريخ' : 'To Date'}
              />
            </div>
          </div>
        </div>

        <div className='row g-2 align-items-center'>
          {/* بحث برقم الطلب */}
          <div className='col-lg-4'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-search'></i>
              </span>
              <input
                type='text'
                className='form-control'
                value={purCodeLike}
                onChange={e => setPurCodeLike(e.target.value)}
                placeholder={isAr ? 'بحث برقم الطلب (PUR Code)' : 'Search by PUR Code'}
              />
            </div>
          </div>

          {/* الفواتير المفتوحة فقط */}
          <div className='col-lg-3'>
            <div className='form-check form-switch pt-1'>
              <input
                className='form-check-input'
                type='checkbox'
                id='onlyOpenInvoices'
                checked={onlyOpenInvoices}
                onChange={e => setOnlyOpenInvoices(e.target.checked)}
              />
              <label className='form-check-label fw-bold' htmlFor='onlyOpenInvoices'>
                {isAr ? 'الفواتير المفتوحة فقط' : 'Only Open Invoices'}
              </label>
            </div>
          </div>

          {/* Buttons */}
          <div className='col-lg-5 text-end'>
            <button
              className='btn btn-outline-secondary me-2'
              type='button'
              onClick={clearFilters}
              title={t('ClearFilters')}
              disabled={!supplierFilter}
            >
              <i className='fas fa-times fa-fw'></i>
            </button>
            <button
              className='btn btn-theme me-2'
              type='button'
              onClick={applyFilters}
              title={t('ApplyFilters')}
              disabled={!supplierFilter} // يتم تعطيل الزر إذا لم يتم اختيار المورد
            >
              <i className='fas fa-filter fa-fw'></i> {isAr ? 'بحث' : 'Search'}
            </button>

            <button
              className='btn btn-outline-success'
              type='button'
              onClick={() => setShowExport(true)}
              title={t('Export')}
              disabled={loading || items.length === 0}
            >
              <i className='fas fa-file-excel fa-fw'></i>
            </button>
          </div>
        </div>
      </CardHeader>

      <Card className='mb-4'>
        <Loading loading={loading} error={error}>
          <div className='table-responsive' style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <table className='table table-hover mb-0'>
              <thead className='sticky-top' style={{ top: 0, zIndex: 10 }}>
                <tr className='bg-theme bg-opacity-35' style={{ backdropFilter: 'blur(20px)' }}>
                  <th className='small' style={{ minWidth: '100px' }}>
                    <div className='d-flex align-items-center'>{isAr ? 'النوع' : 'Type'}</div>
                  </th>
                  <th className='small' style={{ minWidth: '180px' }}>
                    <div className='d-flex align-items-center'>{isAr ? 'رقم المستند' : 'Doc Code'}</div>
                  </th>
                  <th className='small' style={{ minWidth: '110px' }}>
                    <div className='d-flex align-items-center'>{t('Date')}</div>
                  </th>
                  <th className='small' style={{ minWidth: '150px' }}>
                    <div className='d-flex align-items-center'>{isAr ? 'الفرع/المنظمة' : 'Organization'}</div>
                  </th>
                  <th className='small text-end' style={{ minWidth: '110px' }}>
                    <div className='d-flex align-items-center justify-content-end'>{t('Debit')}</div>
                  </th>
                  <th className='small text-end' style={{ minWidth: '110px' }}>
                    <div className='d-flex align-items-center justify-content-end'>{t('Credit')}</div>
                  </th>
                  <th className='small text-end' style={{ minWidth: '120px' }}>
                    <div className='d-flex align-items-center justify-content-end'>{isAr ? 'المفتوح' : 'Open Amt'}</div>
                  </th>
                  <th className='small' style={{ minWidth: '250px' }}>
                    <div className='d-flex align-items-center'>{t('Description')}</div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {!loading && items.length > 0 ? (
                  items.map((r, idx) => {
                    const orgName = isAr
                      ? r.organizationName2 || r.organizationName
                      : r.organizationName || r.organizationName2;

                    // عمل إزاحة (Indentation) بسيطة بناءً على levelOrder لإظهار التدرج
                    const indentationClass = r.levelOrder === 20 ? 'ms-3' : r.levelOrder === 30 ? 'ms-5' : '';
                    const indentationArClass = r.levelOrder === 20 ? 'me-3' : r.levelOrder === 30 ? 'me-5' : '';
                    const paddingClass = isAr ? indentationArClass : indentationClass;

                    return (
                      <tr key={`${r.docId || idx}-${r.rowId}`} className='align-middle'>
                        <td>{getDocTypeBadge(r.docType)}</td>

                        <td>
                          <div className={`fw-bold text-dark ${paddingClass}`}>
                            {r.levelOrder > 10 && (
                              <i className='fas fa-level-up-alt fa-rotate-90 text-muted me-1 ms-1 opacity-50'></i>
                            )}
                            {safe(r.docCode)}
                          </div>
                          {r.invoiceCode && r.invoiceCode !== r.docCode && (
                            <div className={`small text-muted ${paddingClass}`}>Inv: {r.invoiceCode}</div>
                          )}
                        </td>

                        <td>
                          <div className='fw-medium'>{r.docDate ? r.docDate.split('T')[0] : '—'}</div>
                        </td>

                        <td>
                          <div className='fw-medium text-muted'>{safe(orgName)}</div>
                        </td>

                        <td className='text-end'>
                          {r.debit > 0 ? (
                            <div className='fw-bold text-success'>{formatNumber(r.debit)}</div>
                          ) : (
                            <div className='text-muted'>—</div>
                          )}
                        </td>

                        <td className='text-end'>
                          {r.credit > 0 ? (
                            <div className='fw-bold text-danger'>{formatNumber(r.credit)}</div>
                          ) : (
                            <div className='text-muted'>—</div>
                          )}
                        </td>

                        <td className='text-end'>
                          <div className='fw-bold'>{formatNumber(r.openAmount)}</div>
                        </td>

                        <td>
                          <div className='d-flex flex-column'>
                            <span className={r.notes ? '' : 'text-muted fst-italic'}>
                              {safe(r.notes, isAr ? 'لا يوجد وصف' : 'No description')}
                            </span>
                            {r.invoiceNum && (
                              <small className='text-info mt-1'>
                                <i className='fas fa-hashtag me-1'></i> Inv Num: {r.invoiceNum}
                              </small>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : !loading && !supplierFilter ? (
                  <tr>
                    <td colSpan={8} className='text-center py-5'>
                      <div className='text-muted'>
                        <i className='fas fa-user-tie fa-3x mb-3 opacity-50 text-danger'></i>
                        <h6 className='text-danger'>
                          {isAr ? 'الرجاء اختيار المورد أولاً' : 'Please select a supplier first'}
                        </h6>
                        <small>
                          {isAr
                            ? 'هذا التقرير يعتمد بشكل أساسي على المورد'
                            : 'This report strictly requires a supplier'}
                        </small>
                      </div>
                    </td>
                  </tr>
                ) : !loading ? (
                  <tr>
                    <td colSpan={8} className='text-center py-5'>
                      <div className='text-muted'>
                        <i className='fas fa-folder-open fa-3x mb-3 opacity-50'></i>
                        <h6>{t('NoData')}</h6>
                        <small>{t('TryChangingFilters')}</small>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>

              {!loading && items.length > 0 && (
                <tfoot className='fw-bold'>
                  <tr className='bg-light'>
                    <td colSpan={4} className='text-end'>
                      <span className='h6'>{t('Total')}:</span>
                    </td>
                    <td className='text-end text-success'>
                      <span className='h6'>{formatNumber(totals.totalDebit)}</span>
                    </td>
                    <td className='text-end text-danger'>
                      <span className='h6'>{formatNumber(totals.totalCredit)}</span>
                    </td>
                    <td className='text-end'>
                      <span className='h6'>{formatNumber(totals.totalOpenAmount)}</span>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Loading>

        <CardFooter>
          <div className='d-flex justify-content-between align-items-center flex-wrap gap-3'>
            <ReportPagination reportKey={REPORT_KEY} maxVisiblePages={3} showItemsPerPage={true} />

            {!loading && items.length > 0 && (
              <div className='text-muted small'>
                <i className='fas fa-info-circle me-1'></i>
                {isAr ? 'عرض' : 'Showing'} {items.length} {isAr ? 'من' : 'of'} {meta?.totalRows || 0}{' '}
                {isAr ? 'سجل' : 'records'}
              </div>
            )}
          </div>
        </CardFooter>
      </Card>

      <ReportExportExcelModal
        show={showExport}
        onHide={() => setShowExport(false)}
        reportKey={REPORT_KEY}
        endpoint={ENDPOINT}
        columns={exportColumns}
        exportPageSize={4000}
        sheetName={({ isAr }) => (isAr ? 'كشف حساب مورد' : 'Supplier Account Statement')}
        fileName={({ reportKey }) => `${reportKey}_${new Date().toISOString().slice(0, 10)}.xlsx`}
      />
    </>
  );
};

export default SupplierAccountStatement_Paged;
